import {
  GridColDef,
  GridRowIdGetter,
  GridValidRowModel,
} from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { ApiResponse, IEntity } from "common/models/ApiResponse";
import { IFilterModel, IListResponse } from "common/models/GridModels";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import DialogLoaderProvider, { ModalProp } from "contexts/DialogLoader";
import { PostModel, useLovelyGridContext } from "contexts/LovelyGridContext";
import { useEffect, useState } from "react";
import EmptyTable from "components/third-party/EmptyTable";

type LovelyGridProps<
  TRowModel extends GridValidRowModel,
  TFilter extends IFilterModel,
  TEntity extends IEntity
> = {
  id: GridRowIdGetter<TRowModel>;
  columns: GridColDef<TRowModel>[];
  queryKey: string;
  fetcher: (
    model: PostModel<TFilter> | undefined
  ) => Promise<ApiResponse<IListResponse<TRowModel>>>;
  modalFetcher: (id: any) => Promise<ApiResponse<TEntity>>;
  modalElement: React.FC<ModalProp<TEntity>>;
};

export default function LovelyGrid<
  TRowModel extends GridValidRowModel,
  TFilter extends IFilterModel,
  TEntity extends IEntity
>({
  columns,
  id,
  queryKey,
  fetcher,
  modalFetcher,
  modalElement,
}: LovelyGridProps<TRowModel, TFilter, TEntity>) {
  const {
    postModel,
    sortModel,
    paginationModel,
    handlePagination,
    handleSorting,
  } = useLovelyGridContext<TFilter>();

  const [rows, setRows] = useState<TRowModel[]>([]);
  const [count, setCount] = useState<number>(0);

  const query = useQuery({
    queryKey: [queryKey],
    queryFn: () => fetcher(postModel),
    enabled: postModel !== undefined,
  });

  useEffect(() => {
    if (query.data) {
      setRows(query.data.apiData?.data || []);
      setCount(query.data?.apiData?.count ?? 0);
    }
  }, [query.data]);

  useEffect(() => {
    query.refetch();
  }, [postModel]);

  return (
    <DialogLoaderProvider
      title="title"
      message="message"
      modalFetcher={modalFetcher}
      queryKey={queryKey}
      Component={modalElement}
    >
      <StyledDataGrid
        sx={{
          "& .MuiDataGrid-columnHeaderTitle": {
            whiteSpace: "wrap",
            fontWeight: 600,
            lineHeight: "normal",
            fontSize: "13px",
          },
          "& .MuiDataGrid-cell": {
            whitespace: "wrap",
            fontSize: 14,
          },
        }}
        density="compact"
        disableColumnResize={false}
        disableRowSelectionOnClick
        disableColumnFilter={true}
        sortingMode="server"
        filterMode="server"
        paginationMode="server"
        pageSizeOptions={[10, 25, 50, 100]}
        loading={query.isFetching}
        columns={columns}
        rows={rows}
        getRowId={id}
        rowCount={count}
        slots={{
          noRowsOverlay: EmptyTable,
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePagination}
        sortModel={sortModel}
        onSortModelChange={handleSorting}
      ></StyledDataGrid>
    </DialogLoaderProvider>
  );
}
